import type { Unit } from './unit.js'

/**
 * O que uma fonte externa sabe sobre um código de barras — e a validação disso
 * na fronteira.
 *
 * ## Dado de terceiro é entrada NÃO CONFIÁVEL, igual à saída do modelo
 *
 * A base do Open Food Facts é colaborativa: qualquer pessoa edita. Medido nas
 * amostras reais desta busca, dois defeitos apareceram de cara — um produto com
 * `product_quantity: 147.41752025` (conversão de onças com precisão inventada) e
 * outro com marca preenchida e **nome vazio**.
 *
 * Por isso este módulo tem a mesma postura de `parseExtraction`: campo que não
 * dá para acreditar vira `null`, e `null` significa "não sei" — que a tela
 * mostra como campo em branco para a pessoa preencher, e não como dado.
 *
 * ## É SUGESTÃO, e nada entra no catálogo sem alguém olhar
 *
 * Princípio 5, o mesmo da nota fiscal. O resultado preenche o formulário; quem
 * salva é a pessoa. Uma busca que gravasse direto poria no catálogo o nome que
 * um estranho digitou, e o nome do produto é a chave de tudo o que vem depois.
 *
 * ## O que ela NÃO traz
 *
 * **Categoria.** A taxonomia deles (`pt:Bebida`) não é a árvore de orçamento
 * desta casa, e traduzir uma na outra seria adivinhar. **Preço**, idem: não é
 * o que a base guarda.
 */

/** O que a busca conseguiu preencher. Todo campo pode ser `null`. */
export type ProductSuggestion = {
  name: string | null
  brand: string | null
  /** Em `unit`. `null` quando não veio, ou veio em unidade que não conhecemos. */
  size: number | null
  unit: Unit | null
  imageUrl: string | null
}

/** As unidades do Open Food Facts que casam com as nossas. */
const UNIDADES: Record<string, Unit> = {
  g: 'g',
  kg: 'kg',
  ml: 'ml',
  l: 'l',
}

function texto(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const limpo = value.trim()
  // Nome vazio vira `null`, e não string em branco: os dois parecem iguais na
  // tela e só um deles deixa a pessoa perceber que precisa digitar.
  return limpo === '' ? null : limpo
}

/**
 * A resposta da API, conferida. `null` quando o código não existe na base.
 *
 * Não lança: **não achar não é erro**. A maior parte dos códigos de limpeza e
 * higiene não está lá, e isso é o caso comum, não a exceção.
 */
export function parseOpenFoodFacts(raw: unknown): ProductSuggestion | null {
  if (typeof raw !== 'object' || raw === null) return null
  const dados = raw as Record<string, unknown>
  // `status: 1` é achou. Qualquer outra coisa é "não tem".
  if (dados['status'] !== 1) return null

  const produto = dados['product']
  if (typeof produto !== 'object' || produto === null) return null
  const p = produto as Record<string, unknown>

  const unidadeCrua = texto(p['product_quantity_unit'])?.toLowerCase() ?? ''
  const unit = UNIDADES[unidadeCrua] ?? null

  return {
    name: texto(p['product_name']),
    // `brands` vem como lista separada por vírgula quando o produto tem mais de
    // uma marca cadastrada ("Tio João, União"). A primeira é a que a embalagem
    // mostra; as outras são ruído de edição colaborativa.
    brand: texto(p['brands'])?.split(',')[0]?.trim() ?? null,
    size: unit === null ? null : tamanho(p['product_quantity']),
    unit,
    imageUrl: texto(p['image_url']),
  }
}

/**
 * O tamanho, arredondado.
 *
 * **A precisão que vem é deles, não nossa.** Um produto americano convertido de
 * onças chega como `147.41752025 g`, e guardar isso seria fingir que sabemos o
 * peso com oito casas. Inteiro é o que a embalagem diz.
 *
 * Zero e negativo viram `null`: `pricePerBaseUnit` divide por eles.
 */
function tamanho(value: unknown): number | null {
  const numero = typeof value === 'number' ? value : Number(texto(value) ?? '')
  if (!Number.isFinite(numero) || numero <= 0) return null
  return Math.round(numero)
}
